import { ProjectChildPayloadDto } from '../../dtos/project.payload.dto';
import { StandardSurveyPayloadDto } from '../../dtos/standard-survey.payload.dto';
import {
  StandardSurveyPayloadInput,
  StandardSurveyPayloadMapper,
} from '../standard-survey-payload-mapper/standard-survey.payload.mapper';

export interface ProjectPayloadInput {
  children: readonly StandardSurveyPayloadInput[];
}

export interface ProjectUpdateChildInput extends StandardSurveyPayloadInput {
  surveyId: number;
}

export interface ProjectUpdatePayloadInput {
  // Read from the project's saved row rather than a child: it owns the launch
  // settings and the project-level pricing that no child carries.
  readonly project: StandardSurveyPayloadInput;
  readonly children: readonly ProjectUpdateChildInput[];
}

/**
 * The pieces every parent/child project payload is assembled from. It emits no
 * payload of its own — where a root comes from is the project type's own rule,
 * so each type's mapper composes these into its create and update payloads.
 */
export abstract class ProjectPayloadMapper {
  protected readonly standardMapper = new StandardSurveyPayloadMapper();

  protected toProjectPayload<TRoot, TChildDto extends ProjectChildPayloadDto>(
    root: TRoot,
    children: readonly TChildDto[],
  ): TRoot & { surveys: TChildDto[] } {
    return {
      ...root,
      surveys: [...children],
    };
  }

  protected childPayloads(
    input: ProjectPayloadInput,
  ): StandardSurveyPayloadDto[] {
    return input.children.map((child) => this.standardMapper.toPayload(child));
  }

  protected withoutLocalization<T extends { survey_localization: string }>(
    payload: T,
  ): Omit<T, 'survey_localization'> {
    const { survey_localization: _localization, ...rest } = payload;
    return rest;
  }

  protected child(
    payload: StandardSurveyPayloadDto,
    _index: number,
    _input: ProjectPayloadInput,
  ): ProjectChildPayloadDto {
    return {
      ...(payload.qualifications?.length
        ? { qualifications: payload.qualifications }
        : {}),
      ...(payload.quotas?.length ? { quotas: payload.quotas } : {}),
    };
  }
}
